import { useEffect, useMemo, useState } from 'react';
import {
  BenchmarkingDataResponse,
  BenchmarkingFilterMap,
  BenchmarkingFilterResponse,
  BenchmarkingMetricsParams,
  BenchmarkingPlatformOptionsMap
} from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { Category } from '@blisspointmedia/bpm-types/dist/FilterPane';
import { Design } from '@Components/designConstants';
import { DatePicker } from '@Components/DatePicker';
import { Page } from '@Components/Page';
import {
  FetchFilterBar,
  hasFilter,
  useFilterPaneState
} from '@Components/FetchFilterBar/FetchFilterBar';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { DateRange, getFormattedDaysAgo } from '../../utils/date-utils';
import { awaitJSON, BenchmarkingLambdaFetch } from '../../utils/fetch-utils';
import { useSetError } from '../../redux/modals';
import { ChannelBenchmarking } from './ChannelBenchmarking';
import './xClientBenchmarking.scss';

import {
  ALL,
  ALL_VERTICALS_OPTION,
  DISABLED_AUDIO_VERTICALS,
  ENABLED_BENCHMARKING_CHANNELS,
  getInitDateRange,
  VERTICAL_DISABLED_PROPS,
  VerticalOption,
  VerticalOptions
} from './benchmarkingConstants';

export const XClientBenchmarking = () => {
  const company = useLocationCompany() ?? '';
  const { media_types, tinuiti_vertical_category } = useCompanyInfo();
  const [dataMap, setDataMap] = useState<Record<string, BenchmarkingDataResponse | 'error'>>({});
  const [dateRange, setDateRange] = useState<DateRange>(getInitDateRange);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  // Distinguishes "never fetched" from "fetched and failed." Keyed by company so a new company
  // still gets its own try.
  const [failedFilterCompany, setFailedFilterCompany] = useState<string>();
  const [filterData, setFilterData] = useState<BenchmarkingFilterMap>();
  const [platformOptions, setPlatformOptions] = useState<BenchmarkingPlatformOptionsMap>();
  const [selectedPlatform, setSelectedPlatform] = useState(ALL);
  const [verticalOptions, setVerticalOptions] = useState<VerticalOptions>([ALL_VERTICALS_OPTION]);
  const [selectedVertical, setSelectedVertical] = useState<string>(ALL);
  const [filterState, setFilterState, resetFilterState] = useFilterPaneState();
  const setError = useSetError();
  const { start, end } = dateRange;

  const benchmarkingChannelsToRender = ENABLED_BENCHMARKING_CHANNELS.filter(c => {
    const normalizedMedia = (media_types || []).map(media => (media === 'tv' ? 'linear' : media));
    return normalizedMedia.includes(c.name);
  });

  const channelNavs = benchmarkingChannelsToRender.map(c => ({
    key: c.name,
    label: c.prettyName
  }));

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'cross-client-benchmarking',
    defaultKey: channelNavs[0]?.key ?? '',
    navs: channelNavs.map(nav => nav.key)
  });

  const dataKey = useMemo(() => {
    const relevantFilterData = filterState.basic;
    const filterSlug = !hasFilter(filterState) ? '' : `_${JSON.stringify(relevantFilterData)}`;
    return `${tab}_${start}_${end}_${selectedPlatform}_${filterSlug}_${selectedVertical}`;
  }, [start, end, tab, selectedPlatform, selectedVertical, filterState]);

  const [activeData, activeMetaData, activeKpis] = useMemo(() => {
    const fullResponse = dataMap[dataKey] || {};
    if (fullResponse === 'error') {
      return [{}, undefined, null];
    }
    return [fullResponse.data, fullResponse.metaData, fullResponse.kpis || null];
  }, [dataMap, dataKey]);

  const activeFilterData: Category[] | undefined = useMemo(
    () => (tab && filterData ? (filterData[tab] as unknown as Category[]) : undefined),
    [tab, filterData]
  );

  const activePlatformOptions = useMemo(() => {
    const options = tab && platformOptions ? platformOptions[tab] : undefined;
    if (!options) return undefined;
    return [{ value: ALL, label: 'All Platforms' }, ...options.map(o => ({ value: o, label: o }))];
  }, [tab, platformOptions]);

  useEffect(() => {
    const noActiveFilters = !hasFilter(filterState);
    const params = {
      company,
      start,
      end,
      channel: tab,
      basic_filter_state: noActiveFilters ? '' : JSON.stringify(filterState.basic),
      platform: selectedPlatform,
      tinuiti_vertical_category: selectedVertical === ALL ? undefined : selectedVertical
    };

    if (tab && !activeData && !isLoading) {
      (async () => {
        try {
          setIsLoading(true);
          const res = await BenchmarkingLambdaFetch<BenchmarkingMetricsParams>(
            '/getBenchmarkingMetrics',
            { params }
          );
          const data = await awaitJSON<BenchmarkingDataResponse>(res);
          setDataMap(m => ({
            ...m,
            [dataKey]: data
          }));
        } catch (e) {
          setError({ message: e.message, reportError: e });
          setDataMap(m => ({
            ...m,
            [dataKey]: 'error'
          }));
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [
    activeData,
    isLoading,
    company,
    start,
    end,
    tab,
    dataKey,
    setError,
    tinuiti_vertical_category,
    selectedPlatform,
    selectedVertical,
    filterState
  ]);

  useEffect(() => {
    if (company && !filterData && !isLoadingFilters && failedFilterCompany !== company) {
      (async () => {
        try {
          setIsLoadingFilters(true);
          const response = await BenchmarkingLambdaFetch('/getBenchmarkingFilterOptions', {
            params: { company }
          });
          const [newFilterData, platformData, rawVerticalOptions] =
            await awaitJSON<BenchmarkingFilterResponse>(response);
          const formattedVerticalOptions = rawVerticalOptions.map(v => {
            const option: VerticalOption =
              v === tinuiti_vertical_category
                ? { value: v, label: `${v}*` }
                : { value: v, label: v };
            if (tab === 'audio' && DISABLED_AUDIO_VERTICALS.has(v)) {
              return { ...option, ...VERTICAL_DISABLED_PROPS };
            }
            return option;
          });
          setFilterData(newFilterData);
          setPlatformOptions(platformData);
          setVerticalOptions([ALL_VERTICALS_OPTION, ...formattedVerticalOptions]);
        } catch (e) {
          // Without this, filterData stays falsy forever and the guard above re-fires on every
          // render once isLoadingFilters flips back to false — an unbounded, no-backoff retry
          // loop against a persistently failing endpoint.
          setFailedFilterCompany(company);
          setError({ message: e.message, reportError: e });
        } finally {
          setIsLoadingFilters(false);
        }
      })();
    }
  }, [
    company,
    setError,
    isLoadingFilters,
    filterData,
    failedFilterCompany,
    tinuiti_vertical_category,
    tab
  ]);

  // Hoisted so DPCalendar's per-cell isOutsideRange(...) doesn't re-run
  // date-fns subDays + format for every day in the calendar view.
  const fourDaysAgo = getFormattedDaysAgo(4);

  return (
    <Page
      title='Benchmarking'
      pageType='Benchmarking'
      navs={channelNavs}
      onNav={nav => {
        goToTab(nav as typeof tab);
        resetFilterState();
        setSelectedPlatform(ALL);
        if (nav === 'audio') {
          setSelectedVertical(ALL);
          setVerticalOptions(p =>
            p.map(vert => {
              if (DISABLED_AUDIO_VERTICALS.has(vert.value)) {
                return { ...vert, ...VERTICAL_DISABLED_PROPS };
              }
              return vert;
            })
          );
        } else {
          setVerticalOptions(p =>
            p.map(vert => {
              const { disabled, simpleTooltip, simpleTooltipVerticalShift, ...rest } = vert;
              return rest;
            })
          );
        }
      }}
      selectedNav={tab}
      actions={
        <div className='x-client-benchmarking-page-actions'>
          <span className='source-description'>Source: Platform</span>
          <DatePicker
            range={dateRange}
            onChange={setDateRange}
            datePresets
            isOutsideRange={date => date > fourDaysAgo}
            design={Design.BRAND}
          />
        </div>
      }
      subHeader={
        activeFilterData && activeFilterData.length > 0 ? (
          <FetchFilterBar
            categories={activeFilterData}
            company={company}
            filters={undefined}
            filterSource='currentState'
            refreshData={() => {}}
            setFilterID={() => {}}
            setFilters={() => {}}
            setFilterSource={() => {}}
            setState={setFilterState}
            state={filterState}
            simpleFilterStateOnly
          />
        ) : (
          <div className='x-client-benchmarking-filter-bar-placeholder' />
        )
      }
    >
      {benchmarkingChannelsToRender.length > 0 ? (
        <div className='x-client-benchmarking'>
          <ChannelBenchmarking
            channel={tab}
            company={company}
            data={activeData}
            activeMetaData={activeMetaData}
            kpis={activeKpis}
            activePlatformOptions={activePlatformOptions}
            setSelectedPlatform={setSelectedPlatform}
            selectedPlatform={selectedPlatform}
            verticalOptions={verticalOptions}
            selectedVertical={selectedVertical}
            setSelectedVertical={setSelectedVertical}
          />
        </div>
      ) : (
        <div data-testid='no-channels-message'>{`No enabled media-types for ${company} are currently available for benchmarking`}</div>
      )}
    </Page>
  );
};
